import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import Seo from "../../components/Seo";

const STATUSES = ["New", "Contacted", "Site Visit Scheduled", "Site Visit Done", "Negotiation", "Booked", "Lost"];
const statusColor = { New: "bg-blue-100 text-blue-800", Contacted: "bg-amber-100 text-amber-800", "Site Visit Scheduled": "bg-purple-100 text-purple-800", "Site Visit Done": "bg-indigo-100 text-indigo-800", Negotiation: "bg-orange-100 text-orange-800", Booked: "bg-indigo-100 text-indigo-800", Lost: "bg-slate-200 text-slate-600" };

export default function AdminLeads() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const { data } = useQuery({ queryKey: ["leads", filter], queryFn: async () => (await api.get("/admin/leads", { params: filter ? { status: filter } : {} })).data });

  const update = async (id, body) => { try { await api.patch(`/admin/leads/${id}`, body); qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Lead updated"); } catch { toast.error("Failed"); } };

  return (
    <div>
      <Seo title="Leads | Admin" />
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Leads</h1>
      <p className="text-slate-500">Every enquiry lands here automatically.</p>

      <div className="flex gap-1 mt-5 flex-wrap">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-lg text-sm ${!filter ? "bg-indigo-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>All</button>
        {STATUSES.map((s) => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-lg text-sm ${filter === s ? "bg-indigo-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{s}</button>)}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead><tr className="border-b border-slate-100 text-left text-slate-500"><th className="p-4">Lead ID</th><th className="p-4">Customer</th><th className="p-4">Property</th><th className="p-4">Source</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
          <tbody>
            {(data?.items || []).map((l) => (
              <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="p-4 font-mono text-xs text-slate-500">{l.lead_id}</td>
                <td className="p-4"><div className="font-medium text-slate-900">{l.name}</div><div className="text-xs text-slate-400">{l.mobile}</div></td>
                <td className="p-4 text-slate-600 text-xs">{l.property_title || "—"}</td>
                <td className="p-4"><span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">{l.source}</span></td>
                <td className="p-4">
                  <Select value={l.status} onValueChange={(v) => update(l.id, { status: v })}>
                    <SelectTrigger className={`h-8 text-xs w-[170px] ${statusColor[l.status] || ""}`} data-testid={`lead-status-${l.lead_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <div className="flex gap-2 items-center">
                    <a href={`tel:${l.mobile}`} className="text-slate-500 hover:text-indigo-700"><Phone className="w-4 h-4" /></a>
                    <a href={`https://wa.me/91${(l.mobile || "").replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-indigo-700"><MessageSquare className="w-4 h-4" /></a>
                    <Dialog>
                      <DialogTrigger asChild><button className="text-xs text-indigo-800 underline" data-testid={`lead-notes-${l.lead_id}`}>Notes</button></DialogTrigger>
                      <DialogContent><DialogHeader><DialogTitle>Lead {l.lead_id} — {l.name}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                          <div className="text-sm text-slate-600">Requirement: {l.requirement || "—"} · Budget: {l.budget || "—"}</div>
                          <textarea defaultValue={l.notes} onBlur={(e) => update(l.id, { notes: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Follow-up notes…" />
                          <input type="date" defaultValue={l.followup_date || ""} onChange={(e) => update(l.id, { followup_date: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data?.items?.length && <div className="p-12 text-center text-slate-400">No leads yet.</div>}
      </div>
    </div>
  );
}
